import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmt, fmtDate, hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CreditCard, Search, Loader2, User, Printer, Plus, Pencil, Trash2, Star, Check, UploadCloud, Sparkles, ArrowLeft, ZoomIn, ZoomOut, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const STYLES = [
  { value: "classic", label: "Classic" },
  { value: "modern", label: "Modern" },
  { value: "compact", label: "Compact" },
  { value: "elegant", label: "Elegant" },
  { value: "grid", label: "Monthly Grid" },
];

const FREQ_OPTIONS = ["Monthly", "Quarterly", "Half-Yearly", "Yearly", "One-Time"];
const MONTH_LABELS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
function defaultMonthsFor(freq) {
  switch (freq) {
    case "Monthly": return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    case "Quarterly": return [0, 3, 6, 9];
    case "Half-Yearly": return [0, 6];
    default: return [0];
  }
}

const SAMPLE = {
  student: { name: "Aarav Sharma", admission_number: "ADM-1024", class_name: "Grade 5", section: "A", father_name: "Rajesh Sharma", academic_session: "2026-27", photo: "" },
  fees: {
    breakdown: [
      { fee_name: "Tuition Fee", frequency: "Monthly", amount: 12000 },
      { fee_name: "Admission Fee", frequency: "One-Time", amount: 5000 },
      { fee_name: "Transport Fee", frequency: "Monthly", amount: 9600 },
    ],
    old_dues: [{ id: "x", label: "Old Dues", remaining: 2000, from_session: "2025-26" }],
    total_payable: 26600, paid: 16000, discount: 1000, due: 9600,
    monthly: { months: ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"],
      total: [1800,1800,1800,1800,1800,1800,1800,1800,1800,1800,1800,1800],
      paid: [1800,1800,1800,1800,1800,1800,0,0,0,0,0,0],
      balance: [0,0,0,0,0,0,1800,3600,5400,7200,9000,10800] },
  },
  history: [
    { id: "1", receipt_number: "RCPT-2026-00011", payment_date: "2026-04-10", payment_mode: "Cash", amount_paid: 10000, status: "Active" },
    { id: "2", receipt_number: "RCPT-2026-00021", payment_date: "2026-05-09", payment_mode: "UPI", amount_paid: 6000, status: "Active" },
  ],
};

function hexToRgba(hex, a) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "#4F46E5");
  if (!m) return `rgba(79,70,229,${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
}

function buildDefaultRows(heads, headFreqs = {}, headMonths = {}) {
  const headList = heads && heads.length ? heads : ["Tuition Fee", "Transport Fee", "Other Fee"];
  const headRows = headList.map((h) => {
    const freq = headFreqs[h] || "Monthly";
    const cm = headMonths[h];
    return { key: "head:" + h, label: h, kind: "head", fill: null, highlight: "none", enabled: true, frequency: freq, months: (cm && cm.length) ? cm : defaultMonthsFor(freq) };
  });
  const sumRows = [
    { key: "sum:lastdue", label: "Last Month Due", kind: "summary", fill: null, highlight: "yellow" },
    { key: "sum:total", label: "TOTAL", kind: "summary", fill: "total", highlight: "blue" },
    { key: "sum:paid", label: "PAID", kind: "summary", fill: "paid", highlight: "blue" },
    { key: "sum:balance", label: "Balance Due", kind: "summary", fill: "balance", highlight: "blue" },
    { key: "sum:ty", label: "Total Yearly", kind: "summary", fill: null, highlight: "yellow" },
    { key: "sum:tp", label: "Total Paid", kind: "summary", fill: null, highlight: "yellow" },
    { key: "sum:td", label: "Total Due", kind: "summary", fill: null, highlight: "yellow" },
    { key: "meta:rno", label: "Receipt No.", kind: "meta", fill: null, highlight: "yellow" },
    { key: "meta:rdate", label: "Receiving Date", kind: "meta", fill: null, highlight: "yellow" },
    { key: "meta:rname", label: "Receiver's Name", kind: "meta", fill: null, highlight: "yellow" },
    { key: "meta:rsign", label: "Receiver's Sign.", kind: "meta", fill: null, highlight: "yellow" },
  ].map((r) => ({ ...r, enabled: true }));
  return [...headRows, ...sumRows];
}

function mergeHeads(rows, heads, headFreqs = {}, headMonths = {}) {
  if (!heads || !heads.length) return rows;
  const have = new Set(rows.filter((r) => r.kind === "head").map((r) => (r.label || "").toLowerCase()));
  const missing = heads.filter((h) => !have.has(h.toLowerCase())).map((h) => {
    const freq = headFreqs[h] || "Monthly";
    const cm = headMonths[h];
    return { key: "head:" + h, label: h, kind: "head", fill: null, highlight: "none", enabled: true, frequency: freq, months: (cm && cm.length) ? cm : defaultMonthsFor(freq) };
  });
  if (!missing.length) return rows;
  let idx = rows.findIndex((r) => r.kind !== "head");
  if (idx < 0) idx = rows.length;
  return [...rows.slice(0, idx), ...missing, ...rows.slice(idx)];
}

function GridFeeCard({ s, f, school, tpl, heads, headFreqs = {}, headMonths = {}, history = [] }) {
  const accent = tpl.accent_color || "#1d4ed8";
  const MONTHS = ["APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC", "JAN", "FEB", "MAR"];
  let partRows = (tpl.rows && tpl.rows.length ? mergeHeads(tpl.rows, heads, headFreqs, headMonths) : buildDefaultRows(heads, headFreqs, headMonths)).filter((r) => r.enabled !== false);
  const headAnnual = {};
  (f.breakdown || []).forEach((b) => {
    headAnnual[(b.fee_name || "").toLowerCase()] = b.amount || 0;
  });
  const fine = f.fine || 0;
  const schoolFees = f.total_fee || 0, transport = f.transport_fee || 0, other = f.old_dues_total || 0;
  const total = schoolFees + transport + other + fine;
  const dueSet = new Set((f.due_months || []).map((m) => (m || "").toUpperCase().slice(0, 3)));
  const finePerMonth = fine > 0 && (f.due_months || []).length ? Math.round(fine / f.due_months.length) : 0;
  const nfmt = (n) => (Number(n) ? Number(n).toLocaleString("en-IN") : "");
  if (fine > 0 && !partRows.some((r) => (r.label || "").toLowerCase() === "late fine")) {
    const li = partRows.findIndex((r) => r.kind !== "head");
    const lateRow = { key: "auto:latefine", label: "Late Fine", kind: "latefine", highlight: "yellow", enabled: true };
    partRows = li < 0 ? [...partRows, lateRow] : [...partRows.slice(0, li), lateRow, ...partRows.slice(li)];
  }
  // Per-month TOTAL = sum of every fee head's installment shown that month + late fine in due months
  const monthTotals = Array(12).fill(0);
  partRows.forEach((row) => {
    if (row.kind === "head") {
      const annual = headAnnual[(row.label || "").toLowerCase()];
      const mnths = row.months || defaultMonthsFor(row.frequency || "Monthly");
      if (annual && mnths.length) {
        const inst = Math.round(annual / mnths.length);
        mnths.forEach((mi) => { if (mi >= 0 && mi < 12) monthTotals[mi] += inst; });
      }
    } else if (row.kind === "latefine") {
      (f.due_months || []).forEach((m) => {
        const idx = MONTHS.indexOf((m || "").toUpperCase().slice(0, 3));
        if (idx >= 0) monthTotals[idx] += finePerMonth;
      });
    }
  });
  // Distribute the total collected amount across months Apr->Mar, each month capped at its own total due
  const paidByMonth = Array(12).fill(0);
  let _remPaid = f.paid || 0;
  for (let i = 0; i < 12; i++) {
    if (_remPaid <= 0) break;
    const alloc = Math.min(_remPaid, monthTotals[i]);
    paidByMonth[i] = Math.round(alloc);
    _remPaid -= alloc;
  }
  const monthBalance = [];
  let _rt = 0, _rp = 0;
  for (let i = 0; i < 12; i++) { _rt += monthTotals[i]; _rp += (paidByMonth[i] || 0); monthBalance.push(Math.round(_rt - _rp)); }
  const totalPaid = f.paid || 0;
  const totalDue = total - totalPaid;
  const sumVal = { "sum:ty": total, "sum:tp": totalPaid, "sum:td": totalDue };
  // Per-month receipt no. / date / receiver pulled from the student's payments
  const rcptByM = Array(12).fill(""), dateByM = Array(12).fill(""), recvByM = Array(12).fill("");
  const _mIdx = (lbl) => MONTHS.indexOf((lbl || "").trim().toUpperCase().slice(0, 3));
  (history || []).filter((p) => p.status !== "Void" && !p.is_old_dues).forEach((p) => {
    const fm = (p.fee_month || "").trim().toLowerCase();
    let idxs;
    if (fm === "all") idxs = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    else idxs = (p.fee_month || "").split(",").map(_mIdx).filter((i) => i >= 0);
    if (!idxs.length) {
      const mo = parseInt((p.payment_date || "").slice(5, 7), 10);
      if (mo) idxs = [((mo - 4) + 12) % 12];
    }
    idxs.forEach((i) => { rcptByM[i] = p.receipt_number || ""; dateByM[i] = p.payment_date || ""; recvByM[i] = p.collected_by || p.received_by || ""; });
  });
  const contact = s.phone || s.father_phone || s.father_mobile || s.guardian_mobile || s.mother_mobile || s.alt_phone || "";
  const Dotted = ({ label, value, labelW }) => (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 5, fontSize: 12, marginBottom: 5 }}>
      <span style={{ fontWeight: 700, color: accent, minWidth: labelW || 0, whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ fontWeight: 700, color: accent }}>:</span>
      <span style={{ flex: 1, borderBottom: `1.5px dotted ${accent}`, fontWeight: 600, color: "#0f172a", paddingBottom: 1, lineHeight: 1.1, overflow: "hidden" }}>{value || "\u00A0"}</span>
    </div>
  );
  const boxHead = { background: accent, color: "#fff", fontWeight: 800, fontSize: 13, textAlign: "center", borderRadius: 6, padding: "3px 0", marginBottom: 8 };
  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }} data-testid="grid-fee-card">
      <div style={{ padding: 12, borderRadius: 16, position: "relative", overflow: "hidden", background: `linear-gradient(135deg, ${accent} 0%, #3b82f6 35%, #60a5fa 55%, ${accent} 100%)` }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "repeating-linear-gradient(135deg, transparent 0 18px, rgba(250,204,21,0.30) 18px 26px)" }} />
        <div style={{ position: "relative", background: "#fff", borderRadius: 12, padding: "16px 18px" }}>
          <div style={{ textAlign: "center", marginBottom: 6 }}>
            {tpl.show_logo && school.logo && <img src={school.logo} alt="" style={{ height: 44, objectFit: "contain", marginBottom: 2 }} />}
            <div style={{ fontSize: 34, fontWeight: 900, color: accent, lineHeight: 1, letterSpacing: 0.5 }}>{school.school_name || "SSS PUBLIC SCHOOL"}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,0.9fr) minmax(0,1fr)", gap: 14, alignItems: "start", marginBottom: 12 }}>
            <div style={{ border: `2px solid ${accent}`, borderRadius: 12, padding: "8px 12px" }}>
              <div style={boxHead}>STUDENT DETAILS</div>
              <Dotted label="Name" value={s.name} labelW={92} />
              <Dotted label="Father's Name" value={s.father_name} labelW={92} />
              <div style={{ display: "flex", alignItems: "flex-end", gap: 5, fontSize: 12, marginBottom: 5 }}>
                <span style={{ fontWeight: 700, color: accent, minWidth: 92, whiteSpace: "nowrap" }}>Class</span>
                <span style={{ fontWeight: 700, color: accent }}>:</span>
                <span style={{ flex: 1, borderBottom: `1.5px dotted ${accent}`, fontWeight: 600, color: "#0f172a", paddingBottom: 1 }}>{s.class_name || "\u00A0"}</span>
                <span style={{ fontWeight: 700, color: accent, whiteSpace: "nowrap" }}>Section</span>
                <span style={{ fontWeight: 700, color: accent }}>:</span>
                <span style={{ width: 56, borderBottom: `1.5px dotted ${accent}`, fontWeight: 600, color: "#0f172a", paddingBottom: 1, textAlign: "center" }}>{s.section || "\u00A0"}</span>
              </div>
              <Dotted label="Contact No." value={contact} labelW={92} />
            </div>
            <div style={{ textAlign: "center", paddingTop: 2 }}>
              {(school.city || tpl.subtitle) && <div style={{ display: "inline-block", background: "#fde047", color: accent, fontWeight: 800, fontSize: 12, padding: "2px 14px", borderRadius: 4, marginBottom: 8 }}>{tpl.subtitle || school.city}</div>}
              <div style={{ background: accent, color: "#fff", fontWeight: 900, fontSize: 22, padding: "8px 10px", borderRadius: 8, border: "3px solid #fde047", marginBottom: 10 }}>{(tpl.header_text || "FEE DETAILS").toUpperCase()}</div>
              <Dotted label="Session" value={s.academic_session} labelW={62} />
            </div>
            <div style={{ border: `2px solid ${accent}`, borderRadius: 12, padding: "8px 12px" }}>
              <div style={boxHead}>FEE STRUCTURE (YEARLY)</div>
              <Dotted label="School Fees" value={fmt(schoolFees)} labelW={100} />
              <Dotted label="Transport" value={fmt(transport)} labelW={100} />
              <Dotted label="Other/Old Fees" value={fmt(other)} labelW={100} />
              {fine > 0 && <Dotted label="Late Fine" value={fmt(fine)} labelW={100} />}
              <div style={{ background: "#fef9c3", borderRadius: 6, padding: "2px 4px" }}><Dotted label="Total" value={fmt(total)} labelW={100} /></div>
              {(f.discount || 0) > 0 && (<>
                <Dotted label="Discount" value={fmt(f.discount)} labelW={100} />
                <div style={{ background: "#dbeafe", borderRadius: 6, padding: "2px 4px" }}><Dotted label="Gross Total" value={fmt(total - (f.discount || 0))} labelW={100} /></div>
              </>)}
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5, tableLayout: "fixed" }}>
            <thead>
              <tr style={{ background: accent, color: "#fff" }}>
                <th style={{ width: 34, border: "1px solid #93c5fd", padding: "4px 2px" }}>S.No.</th>
                <th style={{ width: 138, border: "1px solid #93c5fd", padding: "4px 4px", textAlign: "left" }}>Particulars</th>
                {MONTHS.map((m) => <th key={m} style={{ border: "1px solid #93c5fd", padding: "4px 1px" }}>{m}</th>)}
                <th style={{ width: 50, border: "1px solid #93c5fd", padding: "4px 2px" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {partRows.map((row, i) => {
                const hl = row.highlight || "none";
                const labelBg = hl === "yellow" ? "#fef3c7" : hl === "blue" ? "#dbeafe" : "#eff6ff";
                const cellBg = hl === "yellow" ? "#fef3c7" : hl === "blue" ? "#dbeafe" : "#fff";
                const bold = hl !== "none";
                let rowTotal = null;
                if (row.fill === "total") rowTotal = monthTotals.reduce((a, b) => a + b, 0);
                else if (row.fill === "paid") rowTotal = paidByMonth.reduce((a, b) => a + b, 0);
                else if (row.fill === "balance") rowTotal = monthBalance[11];
                else if (row.kind === "head") rowTotal = headAnnual[(row.label || "").toLowerCase()] || 0;
                else if (row.kind === "latefine") rowTotal = fine;
                return (
                  <tr key={row.key || i}>
                    <td style={{ border: "1px solid #bfdbfe", textAlign: "center", color: accent, fontWeight: 600, background: labelBg }}>{i + 1}</td>
                    <td style={{ border: "1px solid #bfdbfe", padding: "3px 5px", color: accent, fontWeight: bold ? 800 : 600, background: labelBg }}>{row.label}</td>
                    {row.key in sumVal ? (
                      <td colSpan={13} style={{ border: "1px solid #bfdbfe", height: 17, background: cellBg, textAlign: "right", fontSize: 11, fontWeight: 800, color: accent, padding: "0 10px" }}>{fmt(sumVal[row.key])}</td>
                    ) : (<>
                      {MONTHS.map((m, mi) => {
                      let v = "";
                      if (row.fill === "total") v = nfmt(monthTotals[mi]);
                      else if (row.fill === "paid") v = nfmt(paidByMonth[mi]);
                      else if (row.fill === "balance") v = nfmt(monthBalance[mi]);
                      else if (row.kind === "head") {
                        const annual = headAnnual[(row.label || "").toLowerCase()];
                        const months = row.months || defaultMonthsFor(row.frequency || "Monthly");
                        if (annual && months.length && months.includes(mi)) v = nfmt(Math.round(annual / months.length));
                      }
                      else if (row.kind === "latefine") {
                        if (dueSet.has(MONTHS[mi])) v = nfmt(finePerMonth);
                      }
                      else if (row.key === "meta:rno") v = rcptByM[mi];
                      else if (row.key === "meta:rdate") v = dateByM[mi] ? fmtDate(dateByM[mi]) : "";
                      else if (row.key === "meta:rname") v = recvByM[mi];
                      return <td key={m} style={{ border: "1px solid #bfdbfe", height: 17, background: cellBg, textAlign: "center", fontSize: 9, fontWeight: 700, color: accent, padding: "0 1px" }}>{v}</td>;
                      })}
                      <td style={{ border: "1px solid #93c5fd", height: 17, background: hl === "none" ? "#eff6ff" : cellBg, textAlign: "center", fontSize: 10, fontWeight: 800, color: accent, padding: "0 2px" }}>{rowTotal != null ? nfmt(Math.round(rowTotal)) : ""}</td>
                    </>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ border: `2px solid ${accent}`, borderRadius: 10, padding: "8px 12px", marginTop: 10, display: "grid", gridTemplateColumns: "1.7fr 1fr 1fr", gap: 16 }}>
            <Dotted label="Parent/Guardian Name" value={s.father_name} labelW={150} />
            <Dotted label="Signature" value="" labelW={70} />
            <Dotted label="Date" value="" labelW={40} />
          </div>
          <div style={{ border: `2px solid ${accent}`, borderRadius: 10, padding: "8px 12px", marginTop: 8 }}>
            <Dotted label="Remark" value="" labelW={60} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function FeeCardPreview({ tpl, data, settings, heads = [], headFreqs = {}, headMonths = {} }) {
  if (!tpl || !data) return null;
  const accent = tpl.accent_color || "#4F46E5";
  const text = tpl.text_color || "#0f172a";
  const style = tpl.style || "classic";
  const s = data.student || {};
  const f = data.fees || {};
  const history = data.history || [];
  const school = settings || {};
  if ((tpl.style || "") === "grid") return <GridFeeCard s={s} f={f} school={school} tpl={tpl} heads={heads} headFreqs={headFreqs} headMonths={headMonths} history={data.history || []} />;
  const serif = style === "elegant";
  const rows = [...(f.breakdown || [])];
  (f.old_dues || []).filter((o) => o.remaining > 0).forEach((o) => rows.push({ fee_name: o.label, frequency: `from ${o.from_session || "—"}`, amount: o.remaining }));
  const oldRemaining = (f.old_dues || []).filter((o) => o.remaining > 0).reduce((a, o) => a + (o.remaining || 0), 0);
  const grandTotal = (f.total_payable || 0) + oldRemaining;
  const grandDue = (f.due || 0) + oldRemaining;

  const Header = (
    <div
      className={`${style === "elegant" ? "flex flex-col items-center gap-1 text-center" : "flex items-center gap-4"} px-6 py-4`}
      style={
        style === "modern"
          ? { background: `linear-gradient(135deg, ${accent}, ${hexToRgba(accent, 0.75)})`, color: "#fff" }
          : style === "compact"
          ? { background: hexToRgba(accent, 0.08), borderBottom: `2px solid ${accent}` }
          : style === "elegant"
          ? { borderBottom: `3px double ${accent}` }
          : { borderBottom: `4px solid ${accent}` }
      }
    >
      {tpl.show_logo && school.logo && style !== "elegant" && (
        <img src={school.logo} alt="logo" className="h-14 w-14 object-contain rounded bg-white/90 p-1 shrink-0" data-testid="fcp-logo" />
      )}
      <div className={serif ? "" : "flex-1"}>
        {tpl.show_logo && school.logo && style === "elegant" && (
          <img src={school.logo} alt="logo" className="h-12 w-12 object-contain mx-auto mb-1" />
        )}
        <div className="font-bold leading-tight" style={{ fontSize: 20, fontFamily: serif ? "Georgia, serif" : "inherit" }}>
          {school.school_name || "Your School Name"}
        </div>
        {school.address && <div className="text-xs opacity-80">{school.address}</div>}
      </div>
      <div
        className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded"
        style={style === "modern" ? { background: "rgba(255,255,255,0.2)" } : { color: accent, border: `1px solid ${accent}` }}
      >
        {tpl.header_text?.trim() || "Fee Card"}
      </div>
    </div>
  );

  const InfoItem = ({ k, v }) => (
    <div className="flex gap-1.5"><span className="text-slate-400">{k}:</span><span className="font-medium">{v || "—"}</span></div>
  );

  return (
    <div
      className="bg-white rounded-xl overflow-hidden shadow-sm mx-auto"
      style={{ maxWidth: 640, color: text, border: `1px solid ${hexToRgba(accent, 0.3)}`, fontFamily: serif ? "Georgia, serif" : "inherit" }}
      data-testid="fee-card-preview"
    >
      {Header}
      <div className="px-6 py-4 space-y-4 text-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1">
            <InfoItem k="Student" v={s.name} />
            <InfoItem k="Admission No" v={s.admission_number} />
            <InfoItem k="Class" v={`${s.class_name || ""} ${s.section || ""}`.trim()} />
            <InfoItem k="Father" v={s.father_name} />
            <InfoItem k="Session" v={s.academic_session} />
          </div>
          {tpl.show_photo && (
            <div className="w-20 h-24 rounded-lg border-2 shrink-0 flex items-center justify-center overflow-hidden bg-slate-50" style={{ borderColor: hexToRgba(accent, 0.4) }} data-testid="fcp-photo">
              {s.photo ? <img src={s.photo} alt="student" className="w-full h-full object-cover" /> : <User className="text-slate-300" size={34} />}
            </div>
          )}
        </div>

        {tpl.show_breakdown && (
          <div data-testid="fcp-breakdown">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: hexToRgba(accent, 0.1), color: accent }}>
                  <th className="text-left px-3 py-2 rounded-l">Fee Head</th>
                  <th className="text-left px-3 py-2">Frequency</th>
                  <th className="text-right px-3 py-2 rounded-r">Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="px-3 py-1.5">{b.fee_name}</td>
                    <td className="px-3 py-1.5 text-slate-500 text-xs">{b.frequency}</td>
                    <td className="px-3 py-1.5 text-right tabular font-medium">{fmt(b.amount)}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={3} className="px-3 py-3 text-center text-slate-400">No fee heads configured.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tpl.show_due_summary && (
          <div className="flex justify-end" data-testid="fcp-due-summary">
            <div className="w-64 space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="tabular font-medium">{fmt(grandTotal)}</span></div>
              {f.discount > 0 && <div className="flex justify-between text-amber-600"><span>Discount</span><span className="tabular">{fmt(f.discount)}</span></div>}
              {f.discount > 0 && <div className="flex justify-between font-semibold"><span className="text-slate-500">Gross Total</span><span className="tabular">{fmt(grandTotal - (f.discount || 0))}</span></div>}
              <div className="flex justify-between text-emerald-600"><span>Paid</span><span className="tabular">{fmt(f.paid)}</span></div>
              <div className="flex justify-between font-bold text-base pt-1 mt-1 border-t" style={{ color: accent, borderColor: hexToRgba(accent, 0.3) }}><span>Balance Due</span><span className="tabular">{fmt(grandDue)}</span></div>
            </div>
          </div>
        )}

        {tpl.show_payment_history && history.length > 0 && (
          <div data-testid="fcp-history">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: accent }}>Payment History</div>
            <div className="space-y-1">
              {history.filter((p) => p.status !== "Void").map((p) => (
                <div key={p.id} className="flex justify-between text-xs text-slate-500 border-b border-slate-50 py-0.5">
                  <span className="font-mono">{p.receipt_number}</span>
                  <span>{fmtDate(p.payment_date)} · {p.payment_mode}</span>
                  <span className="tabular text-emerald-600">{fmt(p.amount_paid)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tpl.show_signature && (
          <div className="flex justify-end pt-6" data-testid="fcp-signature">
            <div className="text-center">
              <div className="w-40 border-t border-slate-400 pt-1 text-xs text-slate-500">{school.authorized_signatory || "Authorised Signatory"}</div>
            </div>
          </div>
        )}

        {(tpl.footer_text || "").trim() && (
          <div className="text-center text-xs text-slate-400 pt-2 border-t border-slate-100" data-testid="fcp-footer">{tpl.footer_text}</div>
        )}
      </div>
    </div>
  );
}

function TemplateCard({ tpl, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative text-left rounded-xl border-2 p-3 transition-all ${selected ? "ring-2 ring-offset-1" : "hover:border-slate-300"}`}
      style={{ borderColor: selected ? tpl.accent_color : "#e2e8f0", boxShadow: selected ? `0 0 0 2px ${hexToRgba(tpl.accent_color, 0.3)}` : "none" }}
      data-testid={`fee-card-template-${tpl.id}`}
    >
      <div className="h-10 rounded-md mb-2" style={{ background: `linear-gradient(135deg, ${tpl.accent_color}, ${hexToRgba(tpl.accent_color, 0.6)})` }} />
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm text-slate-800 truncate" title={tpl.name}>{tpl.name}</div>
        {selected && <Check size={16} style={{ color: tpl.accent_color }} />}
      </div>
      <div className="text-xs text-slate-400 capitalize flex items-center gap-1">
        {tpl.style}
        {tpl.is_default && <span className="inline-flex items-center gap-0.5 text-amber-500"><Star size={10} fill="currentColor" />default</span>}
      </div>
    </button>
  );
}

function FeeCardTab({ templates, settings, heads, headFreqs, headMonths }) {
  const [sp, setSp] = useSearchParams();
  const preStudent = sp.get("student");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [data, setData] = useState(null);
  const [loadingStu, setLoadingStu] = useState(false);
  const [selId, setSelId] = useState("");

  useEffect(() => {
    if (templates.length && !selId) {
      const def = templates.find((t) => t.is_default) || templates[0];
      setSelId(def.id);
    }
  }, [templates, selId]);

  useEffect(() => {
    if (preStudent) {
      loadStudent(preStudent);
      sp.delete("student");
      setSp(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preStudent]);

  const tpl = templates.find((t) => t.id === selId) || templates[0];

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await api.get("/students", { params: { search: q, limit: 8 } });
      setResults(r.data.students);
      if (r.data.students.length === 1) loadStudent(r.data.students[0].id);
    } catch (e) { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  const loadStudent = async (sid) => {
    setLoadingStu(true);
    try {
      const [p, h] = await Promise.all([
        api.get(`/fees/preview/${sid}`),
        api.get("/payments", { params: { student_id: sid } }),
      ]);
      setData({ student: { ...p.data.student }, fees: p.data.fees, history: h.data });
      setResults([]);
    } catch (e) { toast.error("Failed to load student fees"); }
    finally { setLoadingStu(false); }
  };

  const preview = data || SAMPLE;

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-5">
      <div className="space-y-4 no-print">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Label className="text-xs font-semibold text-slate-500 uppercase">Student</Label>
          <form onSubmit={doSearch} className="flex gap-2 mt-1.5">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-8 h-9" placeholder="Name / adm no / phone…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fee-card-search-input" />
            </div>
            <Button type="submit" size="sm" className="h-9" disabled={searching} data-testid="fee-card-search-btn">{searching ? <Loader2 size={15} className="animate-spin" /> : "Go"}</Button>
          </form>
          {results.length > 0 && (
            <div className="mt-2 border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-60 overflow-auto">
              {results.map((s) => (
                <button key={s.id} onClick={() => loadStudent(s.id)} className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm" data-testid={`fee-card-result-${s.admission_number}`}>
                  <div className="font-medium text-slate-800">{s.name}</div>
                  <div className="text-xs text-slate-400">{s.admission_number} · {s.class_name}</div>
                </button>
              ))}
            </div>
          )}
          {data && (
            <div className="mt-2 text-xs text-slate-500 flex items-center gap-1" data-testid="fee-card-loaded">
              <Check size={13} className="text-emerald-500" />{preview.student.name} loaded
            </div>
          )}
          {!data && <p className="text-xs text-slate-400 mt-2">Showing sample data — search a student to preview their real fee card.</p>}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <Label className="text-xs font-semibold text-slate-500 uppercase">Design Template</Label>
          <p className="text-xs text-slate-400 mb-2">Pick a design — the preview updates instantly.</p>
          <div className="grid grid-cols-2 gap-2" data-testid="fee-card-template-list">
            {templates.map((t) => (
              <TemplateCard key={t.id} tpl={t} selected={t.id === selId} onClick={() => setSelId(t.id)} />
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between no-print">
          <div className="text-sm text-slate-500">Live Preview {tpl ? <>· <span className="font-medium text-slate-700">{tpl.name}</span></> : null}</div>
          <Button variant="outline" size="sm" onClick={() => window.print()} disabled={!tpl} data-testid="fee-card-print-btn"><Printer size={15} className="mr-1.5" />Print</Button>
        </div>
        {loadingStu ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" /></div>
        ) : (
          <div className="fee-card-print-area">
            <FeeCardPreview tpl={tpl} data={preview} settings={settings} heads={heads} headFreqs={headFreqs} headMonths={headMonths} />
          </div>
        )}
      </div>
    </div>
  );
}

const EMPTY_TPL = {
  name: "", style: "classic", accent_color: "#4F46E5", text_color: "#0f172a", header_text: "", footer_text: "",
  show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true, is_default: false,
};

const DESIGN_LIBRARY = [
  { name: "SSS Monthly Fee Card", style: "grid", accent_color: "#1d4ed8", text_color: "#0f172a", header_text: "Fee Details", footer_text: "", show_logo: true, show_photo: false, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true },
  { name: "Classic Blue", style: "classic", accent_color: "#2563EB", text_color: "#0f172a", header_text: "", footer_text: "This is a computer-generated fee card.", show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true },
  { name: "Royal Indigo", style: "classic", accent_color: "#4F46E5", text_color: "#0f172a", header_text: "Fee Card", footer_text: "", show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true },
  { name: "Charcoal Formal", style: "classic", accent_color: "#111827", text_color: "#111827", header_text: "", footer_text: "Please retain this card.", show_logo: true, show_photo: false, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true },
  { name: "Emerald Modern", style: "modern", accent_color: "#10B981", text_color: "#0f172a", header_text: "", footer_text: "Thank you for your timely payments.", show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: true, show_signature: true },
  { name: "Ocean Modern", style: "modern", accent_color: "#0EA5E9", text_color: "#0f172a", header_text: "", footer_text: "", show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true },
  { name: "Sunset Modern", style: "modern", accent_color: "#F97316", text_color: "#0f172a", header_text: "Fee Card", footer_text: "", show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: false },
  { name: "Violet Modern", style: "modern", accent_color: "#7C3AED", text_color: "#0f172a", header_text: "", footer_text: "Thank you!", show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: true, show_signature: true },
  { name: "Rose Modern", style: "modern", accent_color: "#E11D48", text_color: "#0f172a", header_text: "", footer_text: "", show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true },
  { name: "Slate Compact", style: "compact", accent_color: "#334155", text_color: "#0f172a", header_text: "", footer_text: "", show_logo: true, show_photo: false, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: false },
  { name: "Teal Compact", style: "compact", accent_color: "#0D9488", text_color: "#0f172a", header_text: "", footer_text: "", show_logo: true, show_photo: false, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true },
  { name: "Gold Elegant", style: "elegant", accent_color: "#B45309", text_color: "#1c1917", header_text: "Fee Card", footer_text: "Please retain this card for your records.", show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true },
  { name: "Maroon Elegant", style: "elegant", accent_color: "#9F1239", text_color: "#1c1917", header_text: "Fee Card", footer_text: "", show_logo: true, show_photo: true, show_breakdown: true, show_due_summary: true, show_payment_history: false, show_signature: true },
].map((d) => ({ ...EMPTY_TPL, ...d }));

function slugify(s) { return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }

function FeeCardSettingsTab({ templates, settings, reload, heads, headFreqs, headMonths }) {
  const { user } = useAuth();
  const canManage = hasPerm(user, "manage_settings");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_TPL);
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState(false);
  const [adding, setAdding] = useState("");
  const [ffOpen, setFfOpen] = useState(false);
  const [ffFile, setFfFile] = useState(null);
  const [ffName, setFfName] = useState("");
  const [ffPreview, setFfPreview] = useState("");
  const [ffBusy, setFfBusy] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [dragRow, setDragRow] = useState(null);
  const [overRow, setOverRow] = useState(null);
  const [sectionOrder, setSectionOrder] = useState(["identity", "colors", "text", "freq", "rows", "display"]);
  const isGridOnly = (x) => x === "rows" || x === "freq";

  const moveSection = (id, dir) => setSectionOrder((order) => {
    const vis = order.filter((x) => !isGridOnly(x) || form.style === "grid");
    const target = vis[vis.indexOf(id) + dir];
    if (target === undefined) return order;
    const next = [...order];
    const a = next.indexOf(id), b = next.indexOf(target);
    [next[a], next[b]] = [next[b], next[a]];
    return next;
  });

  const uniqueName = (base) => {
    const names = new Set(templates.map((t) => t.name));
    if (!names.has(base)) return base;
    let i = 2;
    while (names.has(`${base} ${i}`)) i++;
    return `${base} ${i}`;
  };

  const addFromPreset = async (preset) => {
    setAdding(preset.name);
    try {
      const payload = { ...preset, name: uniqueName(preset.name), is_default: templates.length === 0 };
      delete payload.id; delete payload.created_at; delete payload.school_id;
      await api.post("/fee-card-templates", payload);
      toast.success(`"${payload.name}" added to your templates`);
      setGallery(false);
      reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setAdding(""); }
  };

  const pickFf = (file) => { setFfFile(file || null); setFfPreview(file && file.type.startsWith("image/") ? URL.createObjectURL(file) : ""); };
  const closeFf = () => { if (ffBusy) return; setFfOpen(false); setFfFile(null); setFfName(""); setFfPreview(""); };
  const genFf = async () => {
    if (!ffFile) { toast.error("Choose an image or PDF first"); return; }
    setFfBusy(true);
    const fd = new FormData();
    fd.append("file", ffFile);
    if (ffName.trim()) fd.append("name", ffName.trim());
    try {
      const start = await api.post("/fee-card-templates/from-file", fd, { timeout: 60000 });
      const jid = start.data.job_id;
      const deadline = Date.now() + 4 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        const st = await api.get(`/fee-card-templates/from-file/status/${jid}`);
        if (st.data.status === "done") { toast.success(`"${st.data.name}" added to your templates`); setFfOpen(false); setFfFile(null); setFfName(""); setFfPreview(""); reload(); return; }
        if (st.data.status === "error") { toast.error(st.data.error || "Could not generate template"); return; }
      }
      toast.error("Generation is taking too long. Please try again.");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not generate template"); }
    finally { setFfBusy(false); }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setStyle = (v) => setForm((p) => ({ ...p, style: v, rows: v === "grid" && (!p.rows || !p.rows.length) ? buildDefaultRows(heads, headFreqs, headMonths) : p.rows }));
  const setRowFreq = (i, freq) => setForm((p) => { const r = [...(p.rows || [])]; r[i] = { ...r[i], frequency: freq, months: defaultMonthsFor(freq) }; return { ...p, rows: r }; });
  const toggleRowMonth = (i, mi) => setForm((p) => { const r = [...(p.rows || [])]; const cur = r[i].months || defaultMonthsFor(r[i].frequency || "Monthly"); const nm = cur.includes(mi) ? cur.filter((x) => x !== mi) : [...cur, mi].sort((a, b) => a - b); r[i] = { ...r[i], months: nm }; return { ...p, rows: r }; });
  const moveRow = (i, d) => setForm((p) => { const r = [...(p.rows || [])]; const j = i + d; if (j < 0 || j >= r.length) return p; [r[i], r[j]] = [r[j], r[i]]; return { ...p, rows: r }; });
  const reorderRow = (from, to) => setForm((p) => { const r = [...(p.rows || [])]; if (from === to || from < 0 || to < 0 || from >= r.length || to >= r.length) return p; const [m] = r.splice(from, 1); r.splice(to, 0, m); return { ...p, rows: r }; });
  const toggleRow = (i) => setForm((p) => { const r = [...(p.rows || [])]; r[i] = { ...r[i], enabled: r[i].enabled === false }; return { ...p, rows: r }; });
  const setRowHl = (i, hl) => setForm((p) => { const r = [...(p.rows || [])]; r[i] = { ...r[i], highlight: hl }; return { ...p, rows: r }; });
  const resetRows = () => setForm((p) => ({ ...p, rows: buildDefaultRows(heads, headFreqs, headMonths) }));

  const openNew = () => { setEditing(null); setForm(EMPTY_TPL); setOpen(true); window.scrollTo(0, 0); };
  const openEdit = (t) => {
    const bf = (rows) => rows.map((r) => (r.kind === "head" && !r.frequency) ? { ...r, frequency: headFreqs[r.label] || "Monthly", months: r.months || defaultMonthsFor(headFreqs[r.label] || "Monthly") } : r);
    const gridRows = t.style === "grid" ? bf(t.rows && t.rows.length ? mergeHeads(t.rows, heads, headFreqs, headMonths) : buildDefaultRows(heads, headFreqs, headMonths)) : (t.rows || null);
    setEditing(t); setForm({ ...EMPTY_TPL, ...t, rows: gridRows }); setOpen(true); window.scrollTo(0, 0);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter a template name"); return; }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      delete payload.id; delete payload.created_at; delete payload.school_id;
      if (editing) await api.put(`/fee-card-templates/${editing.id}`, payload);
      else await api.post("/fee-card-templates", payload);
      toast.success(editing ? "Template updated" : "Template created");
      setOpen(false);
      reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/fee-card-templates/${t.id}`); toast.success("Template deleted"); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const makeDefault = async (t) => {
    try {
      const payload = { ...t, is_default: true }; delete payload.id; delete payload.created_at; delete payload.school_id;
      await api.put(`/fee-card-templates/${t.id}`, payload);
      toast.success(`"${t.name}" set as default`);
      reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const Toggle = ({ k, label }) => (
    <label className="flex items-center justify-between py-1.5 cursor-pointer">
      <span className="text-sm text-slate-600">{label}</span>
      <Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`fct-${k}`} />
    </label>
  );

  const SECTION_TITLES = { identity: "Template Details", colors: "Colours", text: "Header & Footer Text", freq: "Frequency of Fees", rows: "Fee Card Rows", display: "Display Options" };

  const secShell = (id, title, isFirst, isLast, children) => (
    <div key={id} className="rounded-xl border border-slate-200 bg-white" data-testid={`fct-section-${id}`}>
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50 rounded-t-xl">
        <span className="font-semibold text-sm text-slate-700">{title}</span>
        <div className="ml-auto flex items-center gap-0.5">
          <button type="button" disabled={isFirst} onClick={() => moveSection(id, -1)} className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-white disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent" data-testid={`fct-section-up-${id}`}><ChevronUp size={16} /></button>
          <button type="button" disabled={isLast} onClick={() => moveSection(id, 1)} className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-white disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent" data-testid={`fct-section-down-${id}`}><ChevronDown size={16} /></button>
        </div>
      </div>
      <div className="p-3 space-y-3">{children}</div>
    </div>
  );

  const sectionNode = {
    freq: (
      <>
        <p className="text-xs text-slate-400">Set how often each fee head is charged. Months auto-select by frequency — tick or untick to adjust. The grid shows the installment amount in each ticked month.</p>
        <div className="space-y-2" data-testid="fct-freq-manager">
          {(form.rows || []).map((r, i) => (r.kind === "head" ? (
            <div key={r.key} className="rounded-lg border border-slate-200 p-2.5" data-testid={`fct-freq-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="flex-1 truncate text-sm font-medium text-slate-700">{r.label}</span>
                <select value={r.frequency || "Monthly"} onChange={(e) => setRowFreq(i, e.target.value)} className="text-xs border border-slate-200 rounded px-1.5 py-1" data-testid={`fct-freq-select-${i}`}>
                  {FREQ_OPTIONS.map((fo) => <option key={fo} value={fo}>{fo}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-6 gap-1">
                {MONTH_LABELS.map((m, mi) => {
                  const on = (r.months || defaultMonthsFor(r.frequency || "Monthly")).includes(mi);
                  return (
                    <button type="button" key={m} onClick={() => toggleRowMonth(i, mi)} className={`text-[11px] rounded py-1 border transition-colors ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-500 border-slate-200 hover:border-indigo-300"}`} data-testid={`fct-freq-month-${i}-${mi}`}>{m}</button>
                  );
                })}
              </div>
            </div>
          ) : null))}
          {!(form.rows || []).some((r) => r.kind === "head") && <p className="text-xs text-slate-400">No fee heads yet. Add fee heads in your Fee Structure first.</p>}
        </div>
      </>
    ),
    identity: (
      <>
        <div><Label>Template Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="fct-name" /></div>
        <div><Label>Style</Label>
          <Select value={form.style} onValueChange={setStyle}>
            <SelectTrigger data-testid="fct-style"><SelectValue /></SelectTrigger>
            <SelectContent>{STYLES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </>
    ),
    colors: (
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Accent Color</Label><Input type="color" value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="h-10 p-1" data-testid="fct-accent" /></div>
        <div><Label>Text Color</Label><Input type="color" value={form.text_color} onChange={(e) => set("text_color", e.target.value)} className="h-10 p-1" data-testid="fct-text-color" /></div>
      </div>
    ),
    text: (
      <>
        <div><Label>Header Badge Text</Label><Input value={form.header_text} onChange={(e) => set("header_text", e.target.value)} placeholder="Fee Card" data-testid="fct-header" /></div>
        <div><Label>Footer Text</Label><Input value={form.footer_text} onChange={(e) => set("footer_text", e.target.value)} data-testid="fct-footer" /></div>
      </>
    ),
    rows: (
      <>
        <div className="flex items-center justify-between">
          <Label className="text-slate-600">Place / Subtitle</Label>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-indigo-600" onClick={resetRows} data-testid="fct-reset-rows">Reset to current fee heads</Button>
        </div>
        <Input value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} placeholder="e.g. JAITHRA" data-testid="fct-subtitle" />
        <p className="text-xs text-slate-400 -mt-1">Fee heads are fetched from your fee structure. Toggle, recolour, drag a row handle to reorder, or use the arrows.</p>
        <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="fct-rows-manager">
          {(form.rows || []).map((r, i) => (
            <div
              key={r.key}
              onDragOver={(e) => { e.preventDefault(); if (overRow !== i) setOverRow(i); }}
              onDrop={(e) => { e.preventDefault(); if (dragRow !== null) reorderRow(dragRow, i); setDragRow(null); setOverRow(null); }}
              className={`flex items-center gap-1.5 px-2 py-1.5 text-sm transition-colors ${dragRow === i ? "opacity-40" : ""} ${overRow === i && dragRow !== null && dragRow !== i ? "bg-indigo-50 ring-1 ring-inset ring-indigo-300" : ""}`}
              data-testid={`fct-row-${i}`}
            >
              <span
                draggable
                onDragStart={(e) => { setDragRow(i); e.dataTransfer.effectAllowed = "move"; }}
                onDragEnd={() => { setDragRow(null); setOverRow(null); }}
                className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none"
                data-testid={`fct-row-drag-${i}`}
              ><GripVertical size={14} /></span>
              <Switch checked={r.enabled !== false} onCheckedChange={() => toggleRow(i)} data-testid={`fct-row-toggle-${i}`} />
              <span className={`flex-1 truncate ${r.enabled === false ? "text-slate-300 line-through" : "text-slate-700"}`}>{r.label}{r.kind === "head" && <span className="ml-1 text-[10px] text-indigo-400">fee head</span>}</span>
              <select value={r.highlight || "none"} onChange={(e) => setRowHl(i, e.target.value)} className="text-xs border border-slate-200 rounded px-1 py-0.5" data-testid={`fct-row-hl-${i}`}>
                <option value="none">Plain</option>
                <option value="yellow">Yellow</option>
                <option value="blue">Blue</option>
              </select>
              <button type="button" onClick={() => moveRow(i, -1)} className="text-slate-400 hover:text-slate-700 px-1" data-testid={`fct-row-up-${i}`}>▲</button>
              <button type="button" onClick={() => moveRow(i, 1)} className="text-slate-400 hover:text-slate-700 px-1" data-testid={`fct-row-down-${i}`}>▼</button>
            </div>
          ))}
        </div>
      </>
    ),
    display: (
      <>
        <Toggle k="show_logo" label="Show school logo" />
        <Toggle k="show_photo" label="Show student photo" />
        <Toggle k="show_breakdown" label="Show fee breakdown table" />
        <Toggle k="show_due_summary" label="Show due summary" />
        <Toggle k="show_payment_history" label="Show payment history" />
        <Toggle k="show_signature" label="Show signature line" />
        <Toggle k="is_default" label="Set as default template" />
      </>
    ),
  };

  if (open) {
    return (
      <div className="space-y-4" data-testid="fct-editor-panel">
        <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
          <Button variant="ghost" size="sm" className="gap-1.5 text-slate-600" onClick={() => setOpen(false)} data-testid="fct-back-btn"><ArrowLeft size={16} />Back</Button>
          <h2 className="font-heading text-xl font-bold text-slate-900">{editing ? "Edit" : "New"} Fee Card Template</h2>
          <Button className="ml-auto" onClick={save} disabled={saving} data-testid="fct-save-btn">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save Template"}</Button>
        </div>
        <ResizablePanelGroup direction="horizontal" className="h-[calc(100vh-240px)] min-h-[480px] rounded-lg border border-slate-200" data-testid="fct-editor-split">
          <ResizablePanel defaultSize={50} minSize={30} className="!overflow-y-auto p-4">
            <div className="space-y-3" data-testid="fct-sections">
              {sectionOrder.filter((id) => !isGridOnly(id) || form.style === "grid").map((id, idx, arr) =>
                secShell(id, SECTION_TITLES[id], idx === 0, idx === arr.length - 1, sectionNode[id])
              )}
            </div>
          </ResizablePanel>
          <ResizableHandle withHandle data-testid="fct-resize-handle" />
          <ResizablePanel defaultSize={50} minSize={25} className="!overflow-auto p-4 bg-slate-50">
            <div className="flex items-center gap-2 mb-2">
              <Label className="text-xs text-slate-400 mr-auto">Live Preview · drag the handle to resize</Label>
              <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} data-testid="fct-zoom-out"><ZoomOut size={14} /></Button>
              <Slider value={[zoom]} min={0.4} max={2} step={0.05} onValueChange={(v) => setZoom(v[0])} className="w-28" data-testid="fct-zoom-slider" />
              <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))} data-testid="fct-zoom-in"><ZoomIn size={14} /></Button>
              <span className="text-xs font-semibold text-slate-500 w-10 text-right tabular-nums" data-testid="fct-zoom-value">{Math.round(zoom * 100)}%</span>
            </div>
            {(() => {
              const base = form.style === "grid" ? 0.52 : 0.85;
              const scale = base * zoom;
              return (
                <div className="mt-1" style={{ overflow: "hidden" }}>
                  <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: `${100 / scale}%` }}>
                    <FeeCardPreview tpl={form} data={SAMPLE} settings={settings} heads={heads} headFreqs={headFreqs} headMonths={headMonths} />
                  </div>
                </div>
              );
            })()}
          </ResizablePanel>
        </ResizablePanelGroup>
        <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="fct-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="fct-save-btn-bottom">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save Template"}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Manage the design templates available on the Fees Card page.</p>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setFfOpen(true)} data-testid="fct-from-computer-btn"><UploadCloud size={16} className="mr-1.5" />Template from Computer</Button>
            <Button onClick={() => setGallery(true)} data-testid="fct-add-btn"><Plus size={16} className="mr-1.5" />Add Template</Button>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <div key={t.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`fct-card-${t.id}`}>
            <div className="h-16" style={{ background: `linear-gradient(135deg, ${t.accent_color}, ${hexToRgba(t.accent_color, 0.6)})` }} />
            <div className="p-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-800">{t.name}</div>
                {t.is_default && <span className="inline-flex items-center gap-0.5 text-xs text-amber-500"><Star size={11} fill="currentColor" />Default</span>}
              </div>
              <div className="text-xs text-slate-400 capitalize mb-2">{t.style} style</div>
              {canManage && (
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => openEdit(t)} data-testid={`fct-edit-${t.id}`}><Pencil size={13} /></Button>
                  <Button variant="outline" size="sm" className="h-8 px-2 text-rose-600" onClick={() => del(t)} data-testid={`fct-del-${t.id}`}><Trash2 size={13} /></Button>
                  {!t.is_default && <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-amber-600" onClick={() => makeDefault(t)} data-testid={`fct-default-${t.id}`}>Set default</Button>}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={gallery} onOpenChange={setGallery}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="design-gallery-dialog">
          <DialogHeader>
            <DialogTitle>Choose a Design</DialogTitle>
            <p className="text-sm text-slate-500">Browse the ready-made fee card designs. Click one to instantly add it to your templates.</p>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="design-gallery-grid">
            {DESIGN_LIBRARY.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => addFromPreset(p)}
                disabled={!!adding}
                className="group relative rounded-xl border-2 border-slate-200 hover:border-indigo-400 overflow-hidden text-left transition-all disabled:opacity-60"
                data-testid={`design-preset-${slugify(p.name)}`}
              >
                <div className="h-48 overflow-hidden bg-slate-50 pointer-events-none">
                  <div style={{ transform: "scale(0.42)", transformOrigin: "top left", width: "238%" }}>
                    <FeeCardPreview tpl={p} data={SAMPLE} settings={settings} heads={heads} headFreqs={headFreqs} headMonths={headMonths} />
                  </div>
                </div>
                <div className="p-2.5 flex items-center justify-between bg-white border-t border-slate-100">
                  <div>
                    <div className="font-semibold text-sm text-slate-800">{p.name}</div>
                    <div className="text-xs text-slate-400 capitalize">{p.style} style</div>
                  </div>
                  {adding === p.name ? (
                    <Loader2 size={15} className="animate-spin text-indigo-600" />
                  ) : (
                    <span className="opacity-0 group-hover:opacity-100 text-xs font-semibold text-indigo-600 flex items-center gap-1 transition-opacity"><Plus size={13} />Use</span>
                  )}
                </div>
              </button>
            ))}
          </div>
          <DialogFooter className="border-t border-slate-100 pt-3">
            <span className="text-xs text-slate-400 mr-auto self-center">Want full control over colours and fields?</span>
            <Button variant="outline" onClick={() => { setGallery(false); openNew(); }} data-testid="gallery-custom-btn"><Pencil size={14} className="mr-1.5" />Create custom design</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ffOpen} onOpenChange={(o) => { if (!o) closeFf(); }}>
        <DialogContent className="max-w-lg" data-testid="fct-from-file-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-violet-600" />Template from Computer</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Upload a picture or PDF of a fee card. AI recreates its design as a new fee-card template; the school's real data fills in automatically in the preview.</p>
          <label className="mt-1 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 hover:border-violet-400 py-8 cursor-pointer transition-colors" data-testid="fct-file-dropzone">
            <UploadCloud size={28} className="text-slate-400" />
            <span className="text-sm text-slate-500">{ffFile ? ffFile.name : "Click to choose an image or PDF"}</span>
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pickFf(e.target.files?.[0])} data-testid="fct-file-input" />
          </label>
          {ffPreview && <img src={ffPreview} alt="preview" className="max-h-48 mx-auto rounded-lg border border-slate-200" data-testid="fct-file-preview" />}
          <div className="space-y-1">
            <Label>Name (optional)</Label>
            <Input value={ffName} onChange={(e) => setFfName(e.target.value)} placeholder="Auto from AI" data-testid="fct-file-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeFf} disabled={ffBusy}>Cancel</Button>
            <Button className="gap-1.5 bg-gradient-to-r from-violet-600 to-indigo-600" onClick={genFf} disabled={ffBusy || !ffFile} data-testid="fct-file-generate-btn">
              {ffBusy ? <><Loader2 className="animate-spin" size={16} />Generating…</> : <><Sparkles size={16} />Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function FeesCard() {
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState({});
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeHeadFreqs, setFeeHeadFreqs] = useState({});
  const [feeHeadMonths, setFeeHeadMonths] = useState({});
  const [loading, setLoading] = useState(true);

  const reload = () => api.get("/fee-card-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => {
    Promise.all([api.get("/fee-card-templates"), api.get("/settings"), api.get("/fee-heads"), api.get("/fee-head-frequencies"), api.get("/fee-head-collection-months")])
      .then(([t, s, h, fq, cm]) => { setTemplates(t.data); setSettings(s.data || {}); setFeeHeads(h.data || []); setFeeHeadFreqs(fq.data || {}); setFeeHeadMonths(cm.data || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 space-y-5" data-testid="fees-card-page">
      <div className="no-print">
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><CreditCard className="text-indigo-600" />Fees Card</h1>
        <p className="text-slate-500">Preview a student's fee card and manage its design templates.</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>
      ) : (
        <Tabs defaultValue="card">
          <TabsList className="no-print">
            <TabsTrigger value="card" data-testid="tab-fees-card">Fees Card</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-fees-card-settings">Fees Card Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="card" className="mt-4">
            <FeeCardTab templates={templates} settings={settings} heads={feeHeads} headFreqs={feeHeadFreqs} headMonths={feeHeadMonths} />
          </TabsContent>
          <TabsContent value="settings" className="mt-4">
            <FeeCardSettingsTab templates={templates} settings={settings} reload={reload} heads={feeHeads} headFreqs={feeHeadFreqs} headMonths={feeHeadMonths} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
